import './App.css';
import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import GameBoard from './components/GameBoard';
import { useWallet } from './hooks/useWallet';
import { useMatchmaking } from './hooks/useMatchmaking';
import { request } from './services/api';
import { paymentGatewayStatus } from './modules/payments';

const featureCards = [
  { title: 'Play Online', subtitle: '4-player premium table with live matchmaking', icon: '🎮', color: 'card-purple', screen: 'modes' },
  { title: 'Smart Tables', subtitle: 'Reserved seats fill automatically after wait timer', icon: '✨', color: 'card-blue', screen: 'modes' },
  { title: 'Wallet', subtitle: 'Coins, rewards, entry fee aur recharge tracking', icon: '💰', color: 'card-gold', screen: 'wallet' },
  { title: 'History', subtitle: 'Match result, wallet flow aur activity timeline', icon: '📊', color: 'card-pink', screen: 'history' },
];

const defaultModes = [
  { modeId: 'quick', title: 'Quick Table', subtitle: 'Fast 4-player table with lower entry', icon: '⚡', color: 'card-purple', entryFee: 50, prizeAmount: 170, enabled: true },
  { modeId: 'classic', title: 'Classic Table', subtitle: 'Full 4-player ludo experience', icon: '🏆', color: 'card-blue', entryFee: 200, prizeAmount: 700, enabled: true },
  { modeId: 'rush', title: 'Rush Table', subtitle: 'Premium fast queue with 4 seats', icon: '🚀', color: 'card-gold', entryFee: 100, prizeAmount: 360, enabled: true },
  { modeId: 'practice', title: 'Practice Table', subtitle: '4-player practice room with table fill', icon: '🎯', color: 'card-pink', entryFee: 0, prizeAmount: 40, enabled: true },
];

const LAST_ACTIVE_MATCH_ID_KEY = 'ludo-last-active-match-id';
const LAST_MATCH_STATUS_KEY = 'ludo-last-match-status';
const SCREEN_TO_HASH = {
  home: '#/',
  modes: '#/modes',
  online: '#/online',
  play: '#/play',
  wallet: '#/wallet',
  withdraw: '#/withdraw',
  history: '#/history',
  settings: '#/settings',
  login: '#/login',
  board: '#/board',
  admin: '#/admin-panel',
  'admin-payments': '#/admin-payments',
  'admin-users': '#/admin-users',
  'admin-login': '#/admin-login',
};


const PROTECTED_USER_SCREENS = new Set(['modes', 'online', 'play', 'wallet', 'withdraw', 'history', 'settings', 'board']);
function requiresUserLogin(screen = '') {
  return PROTECTED_USER_SCREENS.has(String(screen || '').trim());
}

function resolveScreenFromHash(hash = '', hasAdminToken = false) {
  const value = String(hash || '').toLowerCase();
  if (value.includes('/admin-payments')) return hasAdminToken ? 'admin-payments' : 'admin-login';
  if (value.includes('/admin-users')) return hasAdminToken ? 'admin-users' : 'admin-login';
  if (value.includes('/admin-panel')) return hasAdminToken ? 'admin' : 'admin-login';
  if (value.includes('/admin-login')) return 'admin-login';
  if (value.includes('/withdraw')) return 'withdraw';
  if (value.includes('/wallet')) return 'wallet';
  if (value.includes('/history')) return 'history';
  if (value.includes('/settings')) return 'settings';
  if (value.includes('/login')) return 'login';
  if (value.includes('/play')) return 'online';
  if (value.includes('/online')) return 'online';
  if (value.includes('/modes')) return 'modes';
  if (value.includes('/board')) return 'board';
  return 'home';
}

function GlassCard({ className = '', children }) {
  return <div className={`glass-panel ${className}`.trim()}>{children}</div>;
}

function NavButton({ active, onClick, children }) {
  return <button className={active ? 'nav-active' : ''} onClick={onClick} type="button">{children}</button>;
}

function SectionHeader({ title, subtitle, action }) {
  return <div className="section-title section-title-row"><div><h3>{title}</h3><p>{subtitle}</p></div>{action}</div>;
}

function getVisibleWithdrawalStatus(status = '') {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'successful' || normalized === 'approved') return 'Successful';
  if (normalized === 'processing') return 'Processing';
  if (normalized === 'rejected') return 'Rejected';
  if (normalized === 'failed') return 'Failed';
  return 'Pending';
}

function formatRupeeAmount(value) {
  const amount = Number(value || 0);
  const safe = Number.isFinite(amount) ? amount : 0;
  const rounded = Math.round(safe * 100) / 100;
  return `₹${rounded.toFixed(2).replace(/\.00$/, '')}`;
}

function calculateWithdrawalPreview(amountCoins = 0, coinsPerRupee = 10, taxPercent = 30) {
  const safeCoins = Math.max(0, Number(amountCoins || 0) || 0);
  const safeRate = Math.max(1, Number(coinsPerRupee || 10) || 10);
  const safeTax = Math.max(0, Number(taxPercent || 0) || 0);
  const grossAmountRupees = Math.round(((safeCoins / safeRate) || 0) * 100) / 100;
  const taxAmountRupees = Math.round((grossAmountRupees * safeTax / 100) * 100) / 100;
  const netAmountRupees = Math.max(0, Math.round((grossAmountRupees - taxAmountRupees) * 100) / 100);
  return { grossAmountRupees, taxAmountRupees, netAmountRupees, coinsPerRupee: safeRate, taxPercent: safeTax };
}

function ActiveMatchChoiceModal({ open, mode, activeRejoin, onContinue, onJoinNew, onClose }) {
  if (!open) return null;
  return (
    <div className="modal-backdrop">
      <div className="glass-panel modal-card">
        <div className="badge">Running Match</div>
        <h3>You already have an active game</h3>
        <p className="muted-copy">Current table: <strong>{activeRejoin?.mode || 'Active Table'}</strong> • Room {activeRejoin?.roomId || '-'} • Seat {activeRejoin?.seat || '-'}</p>
        <p className="muted-copy">Do you want to continue that match or leave it and join <strong>{mode?.title || 'this table'}</strong>?</p>
        <div className="button-stack two-buttons top-gap">
          <button type="button" className="secondary-inline-btn" onClick={onContinue}>Continue Current Match</button>
          <button type="button" onClick={onJoinNew}>Join New Table</button>
        </div>
        <button type="button" className="secondary-btn top-gap" onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

function WalletScreen({ wallet, walletConfig, onBuy, profile, onRequireLogin, onWalletRefresh, initialTab = 'recharge' }) {
  const [tab, setTab] = useState(initialTab);
  const [withdrawConfig, setWithdrawConfig] = useState({ minimumCoins: 100, maximumCoins: 100000, dailyLimitCoins: 200000, coinsPerRupee: Number(walletConfig?.coinsPerRupee || 10), taxPercent: 30 });
  const [withdrawHistory, setWithdrawHistory] = useState([]);
  const [upiId, setUpiId] = useState(profile?.upiId || '');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawStatus, setWithdrawStatus] = useState('Winning amount hi withdraw hoga.');
  const [successPopup, setSuccessPopup] = useState(null);
  const [loadingWithdraw, setLoadingWithdraw] = useState(false);

  useEffect(() => {
    setUpiId(profile?.upiId || '');
  }, [profile?.upiId]);

  useEffect(() => {
    setTab(initialTab || 'recharge');
  }, [initialTab]);

  useEffect(() => {
    setWithdrawConfig((prev) => ({ ...prev, coinsPerRupee: Number(walletConfig?.coinsPerRupee || prev.coinsPerRupee || 10) }));
  }, [walletConfig?.coinsPerRupee]);

  const withdrawPreview = useMemo(() => calculateWithdrawalPreview(withdrawAmount, withdrawConfig.coinsPerRupee, withdrawConfig.taxPercent), [withdrawAmount, withdrawConfig.coinsPerRupee, withdrawConfig.taxPercent]);

  useEffect(() => {
    let cancelled = false;
    async function loadWithdrawMeta() {
      try {
        const [configData, historyData] = await Promise.all([
          request('/api/withdraw/config').catch(() => ({ config: { minimumCoins: 100, maximumCoins: 100000, dailyLimitCoins: 200000 } })),
          profile?.mobile ? request(`/api/withdraw/history/${profile.mobile}`).catch(() => ({ requests: [], upiId: profile?.upiId || '' })) : Promise.resolve({ requests: [], upiId: profile?.upiId || '' }),
        ]);
        if (cancelled) return;
        setWithdrawConfig({
          minimumCoins: Number(configData?.config?.minimumCoins || 100),
          maximumCoins: Number(configData?.config?.maximumCoins || 100000),
          dailyLimitCoins: Number(configData?.config?.dailyLimitCoins || 200000),
          coinsPerRupee: Number(configData?.coinsPerRupee || walletConfig?.coinsPerRupee || 10),
          taxPercent: Number(configData?.taxPercent || 30),
        });
        setWithdrawHistory(historyData?.requests || []);
        if (historyData?.upiId) setUpiId(historyData.upiId);
      } catch {
        if (cancelled) return;
        setWithdrawHistory([]);
      }
    }
    loadWithdrawMeta();
    return () => { cancelled = true; };
  }, [profile?.mobile, profile?.upiId, wallet.lastSyncLabel, walletConfig?.coinsPerRupee]);

  async function handleBuy(pkg) {
    if (!profile?.mobile) {
      onRequireLogin?.('wallet');
      return;
    }
    await onBuy(pkg);
  }

  async function handleWithdrawSubmit() {
    if (!profile?.mobile) {
      onRequireLogin?.('withdraw');
      return;
    }
    const amountCoins = Number(withdrawAmount || 0);
    if (!upiId.trim() || !upiId.includes('@')) {
      setWithdrawStatus('Please enter a valid UPI ID.');
      return;
    }
    if (amountCoins < Number(withdrawConfig.minimumCoins || 100)) {
      setWithdrawStatus(`Minimum withdraw is ${withdrawConfig.minimumCoins || 100} coins.`);
      return;
    }
    if (amountCoins > Number(withdrawConfig.maximumCoins || 0)) {
      setWithdrawStatus(`Maximum withdraw is ${withdrawConfig.maximumCoins || 0} coins.`);
      return;
    }
    if (amountCoins > Number(wallet.withdrawableCoins || 0)) {
      setWithdrawStatus('Only winning amount can be withdrawn.');
      return;
    }
    setLoadingWithdraw(true);
    try {
      await request('/api/profile/upi', { method: 'POST', body: JSON.stringify({ mobile: profile.mobile, upiId }) });
      const result = await request('/api/withdraw/request', { method: 'POST', body: JSON.stringify({ mobile: profile.mobile, upiId, amountCoins }) });
      setWithdrawStatus(result.message || 'Withdraw request sent.');
      setSuccessPopup(result.request || { amountCoins, upiId, id: result.request?.id, ...withdrawPreview });
      setWithdrawAmount('');
      setWithdrawHistory((prev) => [result.request, ...prev]);
      await onWalletRefresh?.('Withdraw request synced.');
    } catch (error) {
      setWithdrawStatus(error.message);
    } finally {
      setLoadingWithdraw(false);
    }
  }

  return (
    <div className="page-stack">
      <div className="dashboard-grid top-balance-grid">
        <GlassCard className="wallet-balance-card">
          <div className="tiny-label">Available Balance</div>
          <h2>{wallet.balance.toLocaleString()} Coins</h2>
          <p>Wallet backend synced. Entry fee, rewards aur recharge history real-time state se aa rahi hai.</p>
          <div className="chip-row wrap-row">
            <span className="soft-chip">Withdrawable Winnings: {Number(wallet.withdrawableCoins || 0)} Coins</span>
            <span className="soft-chip">{wallet.lastSyncLabel}</span>
            <span className="soft-chip">{wallet.lastAction}</span>
          </div>
        </GlassCard>
        <GlassCard>
          <div className="tab-segment">
            <button type="button" className={`history-filter-btn ${tab === 'recharge' ? 'active' : ''}`} onClick={() => setTab('recharge')}>Recharge</button>
            <button type="button" className={`history-filter-btn ${tab === 'withdraw' ? 'active' : ''}`} onClick={() => setTab('withdraw')}>Withdraw</button>
          </div>
          <div className="metric-list top-gap">
            <div><span>Total Recharge</span><strong>{wallet.totals.totalRecharge}</strong></div>
            <div><span>Entry Spend</span><strong>{wallet.totals.entrySpend}</strong></div>
            <div><span>Rewards Earned</span><strong>{wallet.totals.rewardsEarned}</strong></div>
          </div>
          <div className="top-gap muted-copy">{paymentGatewayStatus(wallet.paymentState.message)}</div>
          {wallet.lastTransaction && (
            <div className="wallet-activity-strip top-gap">
              <span className={`history-direction-pill ${wallet.lastTransaction.direction}`}>{wallet.lastTransaction.direction}</span>
              <div>
                <strong>{wallet.lastTransaction.label}</strong>
                <p>{wallet.lastTransaction.time} • {wallet.lastTransaction.reference}</p>
              </div>
              <strong className={wallet.lastTransaction.amountValue >= 0 ? 'result-positive' : 'result-negative'}>{wallet.lastTransaction.amount}</strong>
            </div>
          )}
        </GlassCard>
      </div>

      {tab === 'recharge' ? (
        <GlassCard>
          <SectionHeader title="Coin Packages" subtitle="Recharge API connected with backend wallet state" />
          <div className="cards-grid">
            {wallet.packages.map((item) => (
              <div className="feature-card card-gold compact-card" key={item.id}>
                <div className="feature-icon">🪙</div>
                <h4>{item.coins} Coins</h4>
                <p>+{item.bonus} bonus coins</p>
                <button type="button" onClick={() => handleBuy(item)} disabled={wallet.paymentState.loading}>
                  {wallet.paymentState.loading ? 'Processing...' : `Buy for ₹${item.price}`}
                </button>
              </div>
            ))}
          </div>
        </GlassCard>
      ) : (
        <>
          <GlassCard className="withdraw-panel-card">
            <SectionHeader title="Withdraw via UPI" subtitle="Sirf winning amount withdraw hoga. Added money withdraw nahi hoga. Tax & Service 30% net payout par apply hoga." action={<div className="soft-chip">{withdrawConfig.coinsPerRupee || 10} Coins = ₹1</div>} />
            <div className="withdraw-grid">
              <div className="withdraw-form-box">
                <input className="input-shell actual-input" value={upiId} onChange={(e) => setUpiId(e.target.value)} placeholder="Enter UPI ID (example@upi)" />
                <input className="input-shell actual-input" value={withdrawAmount} onChange={(e) => setWithdrawAmount(e.target.value.replace(/\D/g, ''))} placeholder="Withdraw Amount" />
                <div className="chip-row wrap-row">
                  <span className="soft-chip">Available winnings: {Number(wallet.withdrawableCoins || 0)} Coins</span>
                  <span className="soft-chip">Max / request: {withdrawConfig.maximumCoins || 0}</span>
                  <span className="soft-chip">Daily limit: {withdrawConfig.dailyLimitCoins || 0}</span>
                </div>
                <div className="metric-list top-gap">
                  <div><span>Requested Coins</span><strong>{Number(withdrawAmount || 0)} Coins</strong></div>
                  <div><span>Gross Amount</span><strong>{formatRupeeAmount(withdrawPreview.grossAmountRupees)}</strong></div>
                  <div><span>Tax & Service ({withdrawPreview.taxPercent}%)</span><strong>-{formatRupeeAmount(withdrawPreview.taxAmountRupees)}</strong></div>
                  <div><span>Final Payable</span><strong>{formatRupeeAmount(withdrawPreview.netAmountRupees)}</strong></div>
                </div>
                <button className="primary-btn" type="button" onClick={handleWithdrawSubmit} disabled={loadingWithdraw}>{loadingWithdraw ? 'Submitting...' : 'Request Withdraw'}</button>
                <div className="preview-field"><span>Status</span><strong>{withdrawStatus}</strong></div>
              </div>
              <div className="withdraw-visual-box">
                <div className="withdraw-hero-card">
                  <div className="badge">Fast UPI Cashout</div>
                  <h3>Secure withdraw request</h3>
                  <p>Request जाते ही popup confirmation आएगा aur admin payment panel me request दिखाई देगी.</p>
                  <div className="withdraw-steps">
                    <div><strong>1</strong><span>UPI add karo</span></div>
                    <div><strong>2</strong><span>Amount select karo</span></div>
                    <div><strong>3</strong><span>Admin approve karega</span></div>
                  </div>
                </div>
              </div>
            </div>
          </GlassCard>
          <GlassCard>
            <SectionHeader title="Withdraw Requests" subtitle="Wallet history me aapke withdraw records visible rahenge" />
            <div className="history-list history-list-rich">
              {(withdrawHistory || []).length ? withdrawHistory.map((item) => (
                <div className="history-item history-item-rich" key={item.id}>
                  <div className="history-item-left">
                    <div className="history-badge">{getVisibleWithdrawalStatus(item.status)}</div>
                    <div>
                      <h4>{item.amountCoins} Coins • {formatRupeeAmount(item.netAmountRupees)} payable</h4>
                      <p>{item.upiId} • {new Date(item.createdAt).toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="history-item-meta">
                    <span className="history-reference">Tax & Service {item.taxPercent || withdrawConfig.taxPercent || 30}%</span>
                    <strong className={item.status === 'successful' ? 'result-positive' : 'result-negative'}>{formatRupeeAmount(item.netAmountRupees)}</strong>
                  </div>
                </div>
              )) : <p className="muted-copy">No withdraw requests yet.</p>}
            </div>
          </GlassCard>
          {successPopup && (
            <div className="modal-backdrop" onClick={() => setSuccessPopup(null)}>
              <div className="glass-panel modal-card withdraw-success-card" onClick={(e) => e.stopPropagation()}>
                <div className="badge">Request Submitted</div>
                <h3>Withdraw request received</h3>
                <p className="muted-copy">{successPopup.amountCoins} Coins request {successPopup.upiId} par queue ho gaya hai.</p>
                <p className="muted-copy">Gross: {formatRupeeAmount(successPopup.grossAmountRupees)} • Tax & Service ({successPopup.taxPercent || 30}%): -{formatRupeeAmount(successPopup.taxAmountRupees)} • Final payable: {formatRupeeAmount(successPopup.netAmountRupees)}</p>
                <p className="muted-copy">Reference: {successPopup.id}</p>
                <button type="button" className="primary-btn" onClick={() => setSuccessPopup(null)}>Okay</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function HistoryScreen({ transactions }) {
  const [filter, setFilter] = useState('all');
  const filteredTransactions = useMemo(() => {
    if (filter === 'all') return transactions;
    return transactions.filter((item) => item.direction === filter);
  }, [filter, transactions]);

  return (
    <GlassCard>
      <SectionHeader
        title="History"
        subtitle="Registration, recharge, entry fee, reward aur result timeline"
        action={(
          <div className="chip-row history-filter-row">
            {['all', 'credit', 'debit'].map((item) => (
              <button
                key={item}
                type="button"
                className={`history-filter-btn ${filter === item ? 'active' : ''}`}
                onClick={() => setFilter(item)}
              >
                {item}
              </button>
            ))}
          </div>
        )}
      />
      <div className="history-list history-list-rich">
        {filteredTransactions.map((item, index) => (
          <motion.div
            key={item.id || `${item.label}-${index}`}
            className="history-item history-item-rich"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.04 }}
          >
            <div className="history-item-left">
              <div className="history-badge">{item.typeTitle || item.type}</div>
              <div>
                <h4>{item.label}</h4>
                <p>{item.time}</p>
              </div>
            </div>
            <div className="history-item-meta">
              <span className={`history-direction-pill ${item.direction}`}>{item.direction}</span>
              <span className="history-reference">{item.matchId ? `Match ${item.matchId}` : item.reference}</span>
            </div>
            <strong className={item.amountValue >= 0 ? 'result-positive' : 'result-negative'}>{item.amount}</strong>
          </motion.div>
        ))}
      </div>
    </GlassCard>
  );
}

function SettingsScreen({ profile }) {
  const items = ['Sound effects enabled', 'Dice vibration feedback', 'Quick move animation', 'Auto-select available token'];
  return (
    <div className="dashboard-grid settings-grid">
      <GlassCard>
        <SectionHeader title="Gameplay Settings" subtitle="Player comfort aur premium control options" />
        <div className="settings-list">
          {items.map((label) => <label className="toggle-row" key={label}><span>{label}</span><span className="toggle-pill active-toggle">ON</span></label>)}
        </div>
      </GlassCard>
      <GlassCard>
        <SectionHeader title="Account Preferences" subtitle="Profile, language aur login details" />
        <div className="form-preview">
          <div className="preview-field"><span>Name</span><strong>{profile?.name || 'Guest'}</strong></div>
          <div className="preview-field"><span>Mobile</span><strong>{profile?.mobile || 'Not registered'}</strong></div>
          <div className="preview-field"><span>Language</span><strong>Hindi</strong></div>
          <div className="preview-field"><span>Theme</span><strong>Premium Dark</strong></div>
        </div>
      </GlassCard>
    </div>
  );
}

function LoginScreen({ profile, onLogin, onLoginSuccess, pendingModeTitle }) {
  const [name, setName] = useState(profile?.name || '');
  const [mobile, setMobile] = useState(profile?.mobile || '');
  const [status, setStatus] = useState(pendingModeTitle ? `Complete profile to join ${pendingModeTitle}.` : 'Mobile number se existing account auto login hoga, warna naya register hoga.');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setName(profile?.name || '');
    setMobile(profile?.mobile || '');
  }, [profile?.name, profile?.mobile]);

  const trimmedName = name.trim();
  const normalizedMobile = mobile.replace(/\D/g, '').slice(0, 10);
  const isValid = normalizedMobile.length === 10 && trimmedName.length >= 2;

  async function handleSubmit() {
    if (!trimmedName) {
      setStatus('Please enter your full name.');
      return;
    }
    if (normalizedMobile.length !== 10) {
      setStatus('Please enter a valid 10 digit mobile number.');
      return;
    }
    setLoading(true);
    try {
      let data;
      try {
        data = await request('/api/login', { method: 'POST', body: JSON.stringify({ name: trimmedName, mobile: normalizedMobile }) });
        setStatus(`Welcome back ${data.profile.name}. Existing account logged in.`);
      } catch (error) {
        if (error?.payload?.error !== 'account not found') throw error;
        data = await request('/api/register', { method: 'POST', body: JSON.stringify({ name: trimmedName, mobile: normalizedMobile }) });
        setStatus(`Welcome ${data.profile.name}. New account created successfully.`);
      }
      onLogin(data.profile);
      if (onLoginSuccess) onLoginSuccess(data.profile);
    } catch (error) {
      setStatus(error.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="centered-single">
      <GlassCard className="login-card">
        <div className="badge">Secure Login</div>
        <h3>{profile ? 'Update Profile' : 'Welcome Back'}</h3>
        <p className="muted-copy">Same mobile number se old account auto login ho jayega. New number hoga to naya account banega.</p>
        {pendingModeTitle && <div className="queue-helper-banner">Next step: {pendingModeTitle} join request continue hoga.</div>}
        <div className="input-list">
          <input className="input-shell actual-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full Name" />
          <input className="input-shell actual-input" value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="Mobile Number" />
          <button className="primary-btn full-btn" type="button" onClick={handleSubmit} disabled={loading || !isValid}>{loading ? 'Saving...' : (pendingModeTitle ? 'Continue to Join Table' : 'Continue')}</button>
          <div className="preview-field"><span>Status</span><strong>{status}</strong></div>
        </div>
      </GlassCard>
    </div>
  );
}

function AdminLoginScreen({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState('Enter secure admin credentials.');
  const [loading, setLoading] = useState(false);

  async function handleAdminLogin() {
    if (!username.trim() || !password.trim()) {
      setStatus('Username aur password required hai.');
      return;
    }
    setLoading(true);
    try {
      const result = await request('/api/admin/auth/login', { method: 'POST', body: JSON.stringify({ username, password }) });
      onLogin?.(result.token);
      setStatus('Admin login successful.');
    } catch (error) {
      setStatus(error.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="centered-single">
      <GlassCard className="login-card">
        <div className="badge">Admin Secure URL</div>
        <h3>Admin Panel Login</h3>
        <p className="muted-copy">Yeh panel frontend nav se hidden hai. Secure URL se hi access karo.</p>
        <div className="input-list">
          <input className="input-shell actual-input" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Admin Username" />
          <input className="input-shell actual-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Admin Password" />
          <button className="primary-btn full-btn" type="button" onClick={handleAdminLogin} disabled={loading}>{loading ? 'Checking...' : 'Login to Admin Panel'}</button>
          <div className="preview-field"><span>Status</span><strong>{status}</strong></div>
        </div>
      </GlassCard>
    </div>
  );
}

function AdminScreen({ modes, onModesUpdate, walletConfig, setWalletConfig, onWalletConfigSaved, adminToken, onAdminLogout, routeSection = 'overview', onNavigate }) {
  const [adminTab, setAdminTab] = useState(routeSection);
  const [overview, setOverview] = useState({ stats: [], controls: [], settings: { matchmakingWaitSec: 60, turnTimeoutSec: 15, fillerNames: [], coinsPerRupee: 10 } });
  const [draft, setDraft] = useState({ matchmakingWaitSec: 60, turnTimeoutSec: 15, fillerNames: '', coinsPerRupee: 10 });
  const [tableDrafts, setTableDrafts] = useState(modes || []);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [withdrawConfig, setWithdrawConfig] = useState({ minimumCoins: 100, maximumCoins: 100000, dailyLimitCoins: 200000, autoPayoutEnabled: false, autoPayoutMode: 'manual', coinsPerRupee: 10, taxPercent: 30 });
  const [selectedWithdrawals, setSelectedWithdrawals] = useState([]);
  const [paymentsStatus, setPaymentsStatus] = useState('Payment requests synced.');
  const [adminStatus, setAdminStatus] = useState('Controls synced.');

  const adminRequest = (path, options = {}) => request(path, {
    ...options,
    headers: {
      ...(options.headers || {}),
      Authorization: `Bearer ${adminToken}`,
    },
  });

  async function refreshSelectedUser(mobile = selectedUser?.mobile) {
    if (!mobile) return null;
    try {
      const detail = await adminRequest(`/api/admin/users/${encodeURIComponent(mobile)}`);
      setSelectedUser(detail.user);
      return detail.user;
    } catch {
      return null;
    }
  }

  useEffect(() => {
    setTableDrafts(modes || []);
  }, [modes]);

  useEffect(() => {
    setAdminTab(routeSection || 'overview');
  }, [routeSection]);

  async function refreshAdminData() {
    const [data, usersData, walletData, withdrawalsData] = await Promise.all([
      adminRequest('/api/admin/overview'),
      adminRequest('/api/admin/users'),
      adminRequest('/api/admin/wallet-config'),
      adminRequest('/api/admin/withdrawals'),
    ]);
    setOverview(data);
    setDraft({
      matchmakingWaitSec: data.settings?.matchmakingWaitSec || 60,
      turnTimeoutSec: data.settings?.turnTimeoutSec || 15,
      fillerNames: (data.settings?.fillerNames || []).join(', '),
      coinsPerRupee: data.settings?.coinsPerRupee || walletData?.walletConfig?.coinsPerRupee || 10,
    });
    setWalletConfig(walletData?.walletConfig || { coinsPerRupee: 10, packages: [] });
    if (data.tables?.length) setTableDrafts(data.tables);
    setUsers(usersData.users || []);
    setWithdrawals(withdrawalsData.requests || []);
    setWithdrawConfig({ ...(withdrawalsData.config || { minimumCoins: 100, maximumCoins: 100000, dailyLimitCoins: 200000, autoPayoutEnabled: false, autoPayoutMode: 'manual' }), coinsPerRupee: Number(withdrawalsData.coinsPerRupee || walletData?.walletConfig?.coinsPerRupee || 10), taxPercent: Number(withdrawalsData.taxPercent || 30) });
    if (selectedUser?.mobile) {
      const freshSelectedUser = (usersData.users || []).find((item) => item.mobile === selectedUser.mobile);
      if (!freshSelectedUser) setSelectedUser(null);
      else refreshSelectedUser(selectedUser.mobile);
    }
    return { data, usersData, walletData, withdrawalsData };
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!adminToken) return;
    async function loadAdmin() {
      try {
        await refreshAdminData();
      } catch (error) {
        if (String(error.message || '').includes('admin auth required')) {
          onAdminLogout?.();
          return;
        }
        setOverview({ stats: [], controls: [['Backend', 'Not running']], settings: { matchmakingWaitSec: 60, turnTimeoutSec: 15, fillerNames: [], coinsPerRupee: 10 } });
        setAdminStatus(error.message || 'Admin data could not be loaded.');
      }
    }
    loadAdmin();
  }, [adminToken]); // eslint-disable-line react-hooks/exhaustive-deps

  async function saveSettings() {
    const payload = {
      matchmakingWaitSec: Number(draft.matchmakingWaitSec),
      turnTimeoutSec: Number(draft.turnTimeoutSec),
      fillerNames: String(draft.fillerNames).split(',').map((name) => name.trim()).filter(Boolean),
      coinsPerRupee: Number(draft.coinsPerRupee || 10),
    };
    const res = await adminRequest('/api/admin/settings', { method: 'POST', body: JSON.stringify(payload) });
    const fresh = await refreshAdminData();
    setAdminStatus(`Core controls saved. Match wait ${fresh?.data?.settings?.matchmakingWaitSec || res.settings.matchmakingWaitSec}s.`);
  }

  async function saveTables() {
    const payload = tableDrafts.map((table) => ({
      ...table,
      entryFee: Number(table.entryFee || 0),
      prizeAmount: Number(table.prizeAmount || 0),
      enabled: table.enabled !== false,
    }));
    const res = await adminRequest('/api/admin/tables', { method: 'POST', body: JSON.stringify({ tables: payload }) });
    setTableDrafts(res.tables || payload);
    if (onModesUpdate) onModesUpdate(res.tables || payload);
    const fresh = await refreshAdminData();
    setAdminStatus(`Table controls saved. ${fresh?.data?.tables?.length || (res.tables || payload).length} tables active in config.`);
  }

  async function saveWalletConfig() {
    try {
    const payload = {
      coinsPerRupee: Number(walletConfig.coinsPerRupee || 10),
      signupBonusEnabled: walletConfig.signupBonusEnabled === true,
      signupBonusCoins: Number(walletConfig.signupBonusCoins || 0),
      watchDemoUrl: String(walletConfig.watchDemoUrl || '').trim(),
      packages: (walletConfig.packages || []).map((pkg, index) => ({
        id: pkg.id || `pkg_${Date.now()}_${index}`,
        title: pkg.title || `${pkg.price || pkg.rupees || 10} Rupees Pack`,
        price: Number(pkg.price || pkg.rupees || 1),
        bonus: Number(pkg.bonus || 0),
        enabled: pkg.enabled !== false,
      })),
    };
    const res = await adminRequest('/api/admin/wallet-config', { method: 'POST', body: JSON.stringify(payload) });
    const fresh = await refreshAdminData();
    if (onWalletConfigSaved) onWalletConfigSaved(fresh?.walletData?.walletConfig || res.walletConfig || payload);
    setAdminStatus(`Wallet control saved. ${fresh?.walletData?.walletConfig?.coinsPerRupee || res.walletConfig?.coinsPerRupee || payload.coinsPerRupee} Coins = ₹1.`);
  } catch (error) {
    setAdminStatus(error.message || 'Wallet control save failed.');
  }
}

  async function openUser(mobile, navigateToUsers = false) {
    try {
      const detail = await adminRequest(`/api/admin/users/${encodeURIComponent(mobile)}`);
      setSelectedUser(detail.user);
      if (navigateToUsers) onNavigate?.('admin-users');
    } catch (error) {
      setPaymentsStatus(error.message || 'User detail could not be loaded.');
    }
  }

  async function saveWithdrawConfig() {
    try {
    const payload = {
      minimumCoins: Number(withdrawConfig.minimumCoins || 100),
      maximumCoins: Number(withdrawConfig.maximumCoins || 100000),
      dailyLimitCoins: Number(withdrawConfig.dailyLimitCoins || 200000),
      coinsPerRupee: Number(withdrawConfig.coinsPerRupee || walletConfig.coinsPerRupee || 10),
      autoPayoutEnabled: withdrawConfig.autoPayoutEnabled === true,
      autoPayoutMode: withdrawConfig.autoPayoutMode || 'manual',
    };
    const result = await adminRequest('/api/admin/withdrawals/config', { method: 'POST', body: JSON.stringify(payload) });
    const fresh = await refreshAdminData();
    setPaymentsStatus(`Withdraw controls saved. Minimum ${fresh?.withdrawalsData?.config?.minimumCoins || result.config?.minimumCoins || payload.minimumCoins} coins.`);
  } catch (error) {
    setPaymentsStatus(error.message || 'Withdraw config update failed.');
  }
}

  async function updateWithdrawalStatus(status, requestId = null, payoutMode = 'manual') {
    const requestIds = requestId ? [requestId] : selectedWithdrawals;
    if (!requestIds.length) {
      setPaymentsStatus('Select at least one request.');
      return;
    }
    try {
      await adminRequest('/api/admin/withdrawals/update', {
        method: 'POST',
        body: JSON.stringify({ requestIds, request_ids: requestIds, status, payoutMode }),
      });
      const fresh = await adminRequest('/api/admin/withdrawals');
      setWithdrawals(fresh.requests || []);
      setWithdrawConfig((prev) => ({ ...(fresh.config || prev), coinsPerRupee: Number(fresh.coinsPerRupee || prev.coinsPerRupee || walletConfig.coinsPerRupee || 10), taxPercent: Number(fresh.taxPercent || prev.taxPercent || 30) }));
      const touchedRequest = (fresh.requests || []).find((item) => requestIds.includes(item.id));
      if (touchedRequest?.mobile) await refreshSelectedUser(touchedRequest.mobile);
      setSelectedWithdrawals([]);
      setPaymentsStatus(`Updated ${requestIds.length} request(s) to ${status}.`);
    } catch (error) {
      setPaymentsStatus(error.message || 'Withdrawal status update failed.');
    }
  }

  function toggleWithdrawal(id) {
    setSelectedWithdrawals((prev) => prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]);
  }

  async function syncAutoPayoutStatuses() {
    try {
      const fresh = await adminRequest('/api/admin/withdrawals/sync-auto', { method: 'POST' });
      setWithdrawals(fresh.requests || []);
      setWithdrawConfig((prev) => ({ ...(fresh.config || prev), coinsPerRupee: Number(fresh.coinsPerRupee || prev.coinsPerRupee || walletConfig.coinsPerRupee || 10), taxPercent: Number(fresh.taxPercent || prev.taxPercent || 30) }));
      if (selectedUser?.mobile) await refreshSelectedUser(selectedUser.mobile);
      setPaymentsStatus((fresh.updated || []).length ? 'Auto payout status synced.' : 'No auto payout request needed sync.');
    } catch (error) {
      setPaymentsStatus(error.message || 'Auto payout sync failed.');
    }
  }

  return (
    <div className="page-stack">
      <GlassCard>
        <div className="admin-shell-header">
          <div>
            <div className="tiny-label">Secure Admin Panel</div>
            <h3>Hidden control center</h3>
          </div>
          <div className="chip-row wrap-row">
            <button type="button" className={`history-filter-btn ${adminTab === 'overview' ? 'active' : ''}`} onClick={() => onNavigate?.('admin')}>Overview</button>
            <button type="button" className={`history-filter-btn ${adminTab === 'payments' ? 'active' : ''}`} onClick={() => onNavigate?.('admin-payments')}>Payment Requests</button>
            <button type="button" className={`history-filter-btn ${adminTab === 'users' ? 'active' : ''}`} onClick={() => onNavigate?.('admin-users')}>Users</button>
            <button type="button" className="secondary-inline-btn" onClick={onAdminLogout}>Logout</button>
          </div>
        </div>
      </GlassCard>

      {adminTab === 'overview' && (
        <>
          <div className="admin-stats-grid dashboard-grid">
            {overview.stats.map((stat) => <GlassCard key={stat.label}><div className="tiny-label">{stat.label}</div><h3>{stat.value}</h3></GlassCard>)}
          </div>
          <GlassCard>
            <SectionHeader title="Admin Overview" subtitle="Current connected backend controls" action={<div className="soft-chip">{adminStatus}</div>} />
            <div className="audit-group">
              {overview.controls.map(([label, value]) => <div className="audit-row" key={label}><span>{label}</span><strong>{value}</strong></div>)}
            </div>
          </GlassCard>
          <GlassCard>
            <SectionHeader title="Core Controls" subtitle="Wait timer, turn timer aur reserve names" />
            <div className="input-list">
              <input className="input-shell actual-input" value={draft.matchmakingWaitSec} onChange={(e) => setDraft((prev) => ({ ...prev, matchmakingWaitSec: e.target.value }))} placeholder="Matchmaking wait seconds" />
              <input className="input-shell actual-input" value={draft.turnTimeoutSec} onChange={(e) => setDraft((prev) => ({ ...prev, turnTimeoutSec: e.target.value }))} placeholder="Turn timeout seconds" />
              <input className="input-shell actual-input" value={draft.coinsPerRupee} onChange={(e) => setDraft((prev) => ({ ...prev, coinsPerRupee: e.target.value }))} placeholder="Coins per rupee" />
              <textarea className="input-shell actual-input" rows="4" value={draft.fillerNames} onChange={(e) => setDraft((prev) => ({ ...prev, fillerNames: e.target.value }))} placeholder="Rahul, Ramesh, Aman, Karan" />
              <button className="primary-btn" type="button" onClick={saveSettings}>Save Controls</button>
            </div>
          </GlassCard>
          <GlassCard>
            <SectionHeader title="Wallet Control" subtitle="Admin can set coin value and recharge packages" action={<div className="soft-chip">1₹ = {walletConfig.coinsPerRupee || 10} coins</div>} />
            <div className="input-list">
              <input className="input-shell actual-input" value={walletConfig.coinsPerRupee || ''} onChange={(e) => setWalletConfig((prev) => ({ ...prev, coinsPerRupee: e.target.value }))} placeholder="Coins per rupee" />
              <div className="chip-row wrap-row">
                <button type="button" className={`history-filter-btn ${walletConfig.signupBonusEnabled ? 'active' : ''}`} onClick={() => setWalletConfig((prev) => ({ ...prev, signupBonusEnabled: !prev.signupBonusEnabled }))}>{walletConfig.signupBonusEnabled ? 'Signup Bonus ON' : 'Signup Bonus OFF'}</button>
                <input className="input-shell actual-input" value={walletConfig.signupBonusCoins || 0} onChange={(e) => setWalletConfig((prev) => ({ ...prev, signupBonusCoins: e.target.value }))} placeholder="Signup bonus coins" />
              </div>
              <input className="input-shell actual-input" value={walletConfig.watchDemoUrl || ''} onChange={(e) => setWalletConfig((prev) => ({ ...prev, watchDemoUrl: e.target.value }))} placeholder="Watch Demo URL" />
              <button className="secondary-btn" type="button" onClick={() => setWalletConfig((prev) => ({ ...prev, packages: [...(prev.packages || []), { id: `pkg_${Date.now()}`, title: 'New Package', price: 10, bonus: 0, enabled: true }] }))}>Add Package</button>
            </div>
            <div className="history-list history-list-rich top-gap">
              {(walletConfig.packages || []).map((pkg, index) => (
                <div className="history-item history-item-rich" key={pkg.id || index}>
                  <div className="history-item-left">
                    <div className="history-badge">pkg</div>
                    <div>
                      <h4>{pkg.title || `Package ${index + 1}`}</h4>
                      <p>{Math.round(Number(pkg.price || 0) * Number(walletConfig.coinsPerRupee || 1))} coins + {Number(pkg.bonus || 0)} bonus</p>
                    </div>
                  </div>
                  <div className="chip-row wrap-row">
                    <input className="input-shell actual-input" value={pkg.title || ''} onChange={(e) => setWalletConfig((prev) => ({ ...prev, packages: prev.packages.map((item, i) => i === index ? { ...item, title: e.target.value } : item) }))} placeholder="Package title" />
                    <input className="input-shell actual-input" value={pkg.price} onChange={(e) => setWalletConfig((prev) => ({ ...prev, packages: prev.packages.map((item, i) => i === index ? { ...item, price: e.target.value } : item) }))} placeholder="Rupees" />
                    <input className="input-shell actual-input" value={pkg.bonus || 0} onChange={(e) => setWalletConfig((prev) => ({ ...prev, packages: prev.packages.map((item, i) => i === index ? { ...item, bonus: e.target.value } : item) }))} placeholder="Bonus coins" />
                    <button type="button" className={`history-filter-btn ${pkg.enabled !== false ? 'active' : ''}`} onClick={() => setWalletConfig((prev) => ({ ...prev, packages: prev.packages.map((item, i) => i === index ? { ...item, enabled: !(item.enabled !== false) } : item) }))}>{pkg.enabled !== false ? 'Enabled' : 'Disabled'}</button>
                    <button type="button" className="secondary-inline-btn" onClick={() => setWalletConfig((prev) => ({ ...prev, packages: prev.packages.filter((_, i) => i !== index) }))}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
            <div className="top-gap"><button className="primary-btn" type="button" onClick={saveWalletConfig}>Save Wallet Control</button></div>
          </GlassCard>
          <GlassCard>
            <SectionHeader title="Table Controls" subtitle="Entry fee, prize amount aur enable state backend config se controlled" />
            <div className="history-list history-list-rich">
              {tableDrafts.map((table, index) => (
                <div className="history-item history-item-rich" key={table.modeId || table.title}>
                  <div className="history-item-left">
                    <div className="history-badge">{table.modeId}</div>
                    <div>
                      <h4>{table.title}</h4>
                      <p>{table.subtitle}</p>
                    </div>
                  </div>
                  <div className="chip-row wrap-row">
                    <input className="input-shell actual-input" value={table.entryFee} onChange={(e) => setTableDrafts((prev) => prev.map((item, i) => i === index ? { ...item, entryFee: e.target.value } : item))} placeholder="Entry fee" />
                    <input className="input-shell actual-input" value={table.prizeAmount} onChange={(e) => setTableDrafts((prev) => prev.map((item, i) => i === index ? { ...item, prizeAmount: e.target.value } : item))} placeholder="Prize amount" />
                    <button type="button" className={`history-filter-btn ${table.enabled !== false ? 'active' : ''}`} onClick={() => setTableDrafts((prev) => prev.map((item, i) => i === index ? { ...item, enabled: !(item.enabled !== false) } : item))}>{table.enabled !== false ? 'Enabled' : 'Disabled'}</button>
                  </div>
                </div>
              ))}
            </div>
            <div className="top-gap"><button className="primary-btn" type="button" onClick={saveTables}>Save Table Values</button></div>
          </GlassCard>
        </>
      )}

      {adminTab === 'payments' && (
        <>
          <GlassCard>
            <SectionHeader title="Payment Requests" subtitle={`Bulk select, manual ya auto payout mode choose karo. ${withdrawConfig.coinsPerRupee || walletConfig.coinsPerRupee || 10} Coins = ₹1 • Tax & Service ${withdrawConfig.taxPercent || 30}% net payout.`} action={<div className="soft-chip">{paymentsStatus}</div>} />
            <div className="input-list admin-payment-controls">
              <div className="chip-row wrap-row">
                <input className="input-shell actual-input" value={withdrawConfig.minimumCoins || ''} onChange={(e) => setWithdrawConfig((prev) => ({ ...prev, minimumCoins: e.target.value }))} placeholder="Minimum withdraw" />
                <input className="input-shell actual-input" value={withdrawConfig.maximumCoins || ''} onChange={(e) => setWithdrawConfig((prev) => ({ ...prev, maximumCoins: e.target.value }))} placeholder="Maximum withdraw" />
                <input className="input-shell actual-input" value={withdrawConfig.dailyLimitCoins || ''} onChange={(e) => setWithdrawConfig((prev) => ({ ...prev, dailyLimitCoins: e.target.value }))} placeholder="Daily limit" />
                <input className="input-shell actual-input" value={withdrawConfig.coinsPerRupee || ''} onChange={(e) => setWithdrawConfig((prev) => ({ ...prev, coinsPerRupee: e.target.value }))} placeholder="Coins per rupee" />
              </div>
              <div className="chip-row wrap-row">
                <button type="button" className={`history-filter-btn ${withdrawConfig.autoPayoutEnabled ? 'active' : ''}`} onClick={() => setWithdrawConfig((prev) => ({ ...prev, autoPayoutEnabled: !prev.autoPayoutEnabled }))}>{withdrawConfig.autoPayoutEnabled ? 'Auto payout ON' : 'Auto payout OFF'}</button>
                <button type="button" className={`history-filter-btn ${withdrawConfig.autoPayoutMode === 'manual' ? 'active' : ''}`} onClick={() => setWithdrawConfig((prev) => ({ ...prev, autoPayoutMode: 'manual' }))}>Manual</button>
                <button type="button" className={`history-filter-btn ${withdrawConfig.autoPayoutMode === 'auto_razorpay' ? 'active' : ''}`} onClick={() => setWithdrawConfig((prev) => ({ ...prev, autoPayoutMode: 'auto_razorpay' }))}>Auto Razorpay</button>
                <button type="button" className="primary-btn" onClick={saveWithdrawConfig}>Save Withdraw Config</button>
              </div>
              <div className="chip-row wrap-row">
                <button type="button" className="secondary-inline-btn" onClick={() => updateWithdrawalStatus('processing')} disabled={!selectedWithdrawals.length}>Mark Processing</button>
                <button type="button" className="secondary-inline-btn" onClick={() => updateWithdrawalStatus('successful', null, withdrawConfig.autoPayoutMode || 'manual')} disabled={!selectedWithdrawals.length}>Approve Selected</button>
                <button type="button" className="secondary-inline-btn" onClick={() => updateWithdrawalStatus('failed')} disabled={!selectedWithdrawals.length}>Mark Failed</button>
                <button type="button" className="secondary-inline-btn" onClick={() => updateWithdrawalStatus('rejected')} disabled={!selectedWithdrawals.length}>Reject Selected</button>
                <button type="button" className="secondary-inline-btn" onClick={syncAutoPayoutStatuses}>Sync Auto Status</button>
              </div>
            </div>
          </GlassCard>
          <GlassCard>
            <SectionHeader title="Request Queue" subtitle="Click profile for earning details. Bulk actions bhi available hain." action={<label className="chip-row"><input type="checkbox" checked={withdrawals.length > 0 && selectedWithdrawals.length === withdrawals.length} onChange={() => setSelectedWithdrawals((prev) => prev.length === withdrawals.length ? [] : withdrawals.map((entry) => entry.id))} /> <span className="muted-copy">Select all</span></label>} />
            <div className="history-list history-list-rich">
              {withdrawals.map((item) => (
                <div className="history-item history-item-rich admin-withdraw-row" key={item.id}>
                  <label className="withdraw-check"><input type="checkbox" checked={selectedWithdrawals.includes(item.id)} onChange={() => toggleWithdrawal(item.id)} /></label>
                  <div className="history-item-left">
                    <div className="history-badge">{getVisibleWithdrawalStatus(item.status)}</div>
                    <div>
                      <h4>{item.user?.name || item.name || 'User'} • {item.amountCoins} Coins • {formatRupeeAmount(item.netAmountRupees)} payable</h4>
                      <p>{item.mobile} • {item.upiId} • Rate {item.coinsPerRupeeSnapshot || withdrawConfig.coinsPerRupee || walletConfig.coinsPerRupee || 10} coins = ₹1 • Gross {formatRupeeAmount(item.grossAmountRupees)} • Tax & Service {item.taxPercent || withdrawConfig.taxPercent || 30}% • {new Date(item.createdAt).toLocaleString()}</p>
                      <p>Requested: {item.amountCoins} coins • Conversion: {item.coinsPerRupeeSnapshot || withdrawConfig.coinsPerRupee || walletConfig.coinsPerRupee || 10} coins = ₹1 • Gross: {formatRupeeAmount(item.grossAmountRupees)} • Tax: {formatRupeeAmount(item.taxAmountRupees)} • Final: {formatRupeeAmount(item.netAmountRupees)}</p>
                    </div>
                  </div>
                  <div className="chip-row wrap-row">
                    <button type="button" className="secondary-inline-btn" onClick={() => openUser(item.mobile, true)}>Profile</button>
                    <button type="button" className="secondary-inline-btn" onClick={() => updateWithdrawalStatus('successful', item.id, withdrawConfig.autoPayoutMode || 'manual')}>Approve</button>
                    <button type="button" className="secondary-inline-btn" onClick={() => updateWithdrawalStatus('rejected', item.id)}>Reject</button>
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>
        </>
      )}

      {adminTab === 'users' && (
        <div className="dashboard-grid settings-grid">
          <GlassCard>
            <SectionHeader title="Registered Users" subtitle="Name, mobile, created time aur live status" />
            <div className="history-list history-list-rich">
              {users.map((user) => (
                <button key={user.mobile} type="button" className="history-item history-item-rich secondary-inline-btn" onClick={() => openUser(user.mobile)}>
                  <div className="history-item-left">
                    <div className="history-badge">{user.status}</div>
                    <div>
                      <h4>{user.name}</h4>
                      <p>{user.mobile} • {new Date(user.createdAt).toLocaleString()}</p>
                    </div>
                  </div>
                  <strong>{user.withdrawableCoins || 0} withdrawable</strong>
                </button>
              ))}
            </div>
          </GlassCard>
          <GlassCard>
            <SectionHeader title="User Detail" subtitle="Wallet, matches, earning detail aur withdraw trail" />
            {selectedUser ? (
              <div className="form-preview">
                <div className="preview-field"><span>Name</span><strong>{selectedUser.name}</strong></div>
                <div className="preview-field"><span>Mobile</span><strong>{selectedUser.mobile}</strong></div>
                <div className="preview-field"><span>Created</span><strong>{new Date(selectedUser.createdAt).toLocaleString()}</strong></div>
                <div className="preview-field"><span>Wallet</span><strong>{selectedUser.currentWallet} Coins</strong></div>
                <div className="preview-field"><span>Withdraw UPI</span><strong>{selectedUser.upiId || 'Not saved'}</strong></div>
                <div className="preview-field"><span>Total Matches</span><strong>{selectedUser.totalMatches}</strong></div>
                <div className="preview-field"><span>Wins</span><strong>{selectedUser.wins}</strong></div>
                <div className="preview-field"><span>Winning Balance</span><strong>{selectedUser.earnings?.winningCoins || 0} Coins</strong></div>
                <div className="preview-field"><span>Added Money</span><strong>{selectedUser.earnings?.depositCoins || 0} Coins</strong></div>
                <div className="preview-field"><span>Withdrawn</span><strong>{selectedUser.earnings?.totalWithdrawnCoins || 0} Coins</strong></div>
                <div className="preview-field"><span>IP</span><strong>{selectedUser.ip || 'Not available'}</strong></div>
                <div className="top-gap">
                  <div className="tiny-label">Withdrawal trail</div>
                  <div className="history-list history-list-rich top-gap">
                    {(selectedUser.withdrawals || []).length ? (selectedUser.withdrawals || []).map((item) => (
                      <div key={item.id} className="history-item history-item-rich">
                        <div className="history-item-left"><div className="history-badge">{getVisibleWithdrawalStatus(item.status)}</div><div><h4>{item.amountCoins} Coins • {formatRupeeAmount(item.netAmountRupees)} payable</h4><p>{item.upiId} • Gross {formatRupeeAmount(item.grossAmountRupees)} • Tax & Service {item.taxPercent || 30}%</p></div></div>
                        <strong>{new Date(item.createdAt).toLocaleString()}</strong>
                      </div>
                    )) : <p className="muted-copy">No withdraw requests yet.</p>}
                  </div>
                </div>
                <div className="top-gap">
                  <div className="tiny-label">Request history</div>
                  <div className="history-list history-list-rich top-gap">
                    {(selectedUser.withdrawals || []).length ? (selectedUser.withdrawals || []).map((item) => (
                      <div key={`${item.id}_request`} className="history-item history-item-rich">
                        <div className="history-item-left"><div className="history-badge">{getVisibleWithdrawalStatus(item.status)}</div><div><h4>{item.amountCoins} Coins</h4><p>{item.upiId} • Final {formatRupeeAmount(item.netAmountRupees)} • {item.note || 'No admin note'}</p></div></div>
                        <strong>{new Date(item.updatedAt || item.createdAt).toLocaleString()}</strong>
                      </div>
                    )) : <p className="muted-copy">No request history yet.</p>}
                  </div>
                </div>
                <div className="top-gap">
                  <div className="tiny-label">Recent history</div>
                  <div className="history-list history-list-rich top-gap">
                    {(selectedUser.history || []).filter((item) => Number(item.amountValue || 0) !== 0).map((item) => (
                      <div key={item.id} className="history-item history-item-rich">
                        <div className="history-item-left"><div className="history-badge">{item.type}</div><div><h4>{item.label}</h4><p>{item.time || item.createdAt}</p></div></div>
                        <strong>{item.amount}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : <p className="muted-copy">Select a user from the list to view details.</p>}
          </GlassCard>
        </div>
      )}
    </div>
  );
}

function MatchmakingScreen({ mode, queueState, profile, isJoining, onFindMatch, onCancelQueue, onPlayNow, rejoinMatch, onRejoin }) {
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    if (!queueState.waitEndsAt) {
      setSecondsLeft(0);
      return undefined;
    }
    const tick = () => setSecondsLeft(Math.max(0, Math.ceil((new Date(queueState.waitEndsAt).getTime() - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [queueState.waitEndsAt]);

  const canEnterBoard = Boolean(queueState.result?.roomId);
  const primaryLabel = isJoining ? 'Joining...' : (queueState.loading ? 'Searching...' : 'Find Match');
  const secondaryLabel = queueState.loading ? 'Cancel Queue' : (canEnterBoard ? 'Enter Board' : 'Wait for Match');

  return (
    <div className="page-stack">
      <GlassCard>
        <SectionHeader title="Online Matchmaking" subtitle="4-player table. Seats fill automatically if full table does not form in time." action={<div className="soft-chip">{mode?.title || queueState.queuedModeTitle || 'Quick Table'}</div>} />
        <div className="dashboard-grid">
          <div className="feature-card card-purple compact-card">
            <div className="feature-icon">🌐</div>
            <h4>{queueState.loading ? 'Finding players...' : canEnterBoard ? 'Table Ready' : 'Queue Status'}</h4>
            <p>{queueState.message}</p>
            <div className="queue-status-box top-gap">
              <div className="audit-row"><span>Logged in player</span><strong>{profile?.name || 'Guest'}</strong></div>
              <div className="audit-row"><span>Current mode</span><strong>{mode?.title || queueState.queuedModeTitle || 'Quick Table'}</strong></div>
              <div className="audit-row"><span>Queue state</span><strong>{queueState.loading ? 'Searching' : canEnterBoard ? 'Matched' : 'Idle'}</strong></div>
            </div>
            {queueState.loading && (
              <div className="top-gap">
                <div className="tiny-label">Visible wait timer</div>
                <h3>{String(Math.floor(secondsLeft / 60)).padStart(2, '0')}:{String(secondsLeft % 60).padStart(2, '0')}</h3>
                <p>{queueState.playerCount || 1}/4 players seated</p>
              </div>
            )}
            <div className="button-stack two-buttons">
              <button onClick={onFindMatch} type="button" disabled={queueState.loading || isJoining}>{primaryLabel}</button>
              <button className="secondary-inline-btn" onClick={queueState.loading ? onCancelQueue : onPlayNow} type="button" disabled={!queueState.loading && !canEnterBoard}>{secondaryLabel}</button>
            </div>
          </div>
          <div className="glass-panel queue-side-panel">
            <h3>Table rules</h3>
            <div className="audit-row"><span>Players per match</span><span className="audit-done">4</span></div>
            <div className="audit-row"><span>Wait countdown</span><span className="audit-done">Visible</span></div>
            <div className="audit-row"><span>Private turn timer</span><span className="audit-done">Enabled</span></div>
            <div className="audit-row"><span>Strict profile turns</span><span className="audit-done">Enabled</span></div>
            <div className="audit-row"><span>Queue cancel rule</span><span className="audit-done">No refund after join</span></div>
            {rejoinMatch?.roomId && (
              <div className="top-gap">
                <div className="tiny-label">Running match detected</div>
                <h4>{rejoinMatch.mode || 'Active Table'}</h4>
                <p>Seat: {rejoinMatch.seat || 'Assigned'} • Room {rejoinMatch.roomId}</p>
                <button className="primary-btn top-gap" onClick={onRejoin} type="button">Rejoin Match</button>
              </div>
            )}
          </div>
        </div>
      </GlassCard>
    </div>
  );
}

function ModeSelection({ modes, onQueue, activeModeTitle, isJoining }) {
  return (
    <section className="cards-section">
      <SectionHeader title="Select Match Mode" subtitle="Every mode opens as a 4-player table" />
      <div className="cards-grid">
        {modes.filter((card) => card.enabled !== false).map((card, index) => {
          const busyThisMode = isJoining && activeModeTitle === card.title;
          return (
            <motion.div key={card.title} className={`feature-card ${card.color}`} initial={{ opacity: 0, y: 35 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.12 }}>
              <div className="feature-icon">{card.icon}</div>
              <h4>{card.title}</h4>
              <p>{card.subtitle}</p>
              <div className="mode-meta"><span>Fee: {card.entryFee} coins</span><span>Prize: {card.prizeAmount} coins</span></div>
              <div className="button-stack"><button onClick={() => onQueue(card)} type="button" disabled={busyThisMode}>{busyThisMode ? 'Joining...' : 'Join Table'}</button></div>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

function DashboardTabs({ selectedMode, wallet, profile, modes }) {
  return (
    <section className="bottom-panels">
      <motion.div className="panel glass-panel" initial={{ opacity: 0, y: 35 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
        <h3>Game Modes</h3>
        <div className="mode-list">
          {modes.filter((item) => item.enabled !== false).map((item) => <div className="mode-item" key={item.modeId || item.title}><span>{item.title}</span><span>{item.entryFee} / {item.prizeAmount}</span></div>)}
        </div>
      </motion.div>
      <motion.div className="panel glass-panel" initial={{ opacity: 0, y: 35 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
        <h3>Wallet Preview</h3>
        <div className="wallet-box">
          <p>{profile ? `Logged in as ${profile.name}` : 'Login recommended before online play'}</p>
          <h2>{wallet.balance.toLocaleString()}</h2>
          <div className="current-mode-chip">Selected: {selectedMode?.title || 'No mode selected'}</div>
          <div className="tiny-label top-gap">{wallet.lastAction}</div>
        </div>
      </motion.div>
    </section>
  );
}

function HomeSection({ onCardOpen, onStart, rejoinMatch, onRejoin, watchDemoUrl, profile, onRequireLogin }) {
  return (
    <>
      <section className="hero">
        <motion.div className="hero-left" initial={{ opacity: 0, x: -40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7 }}>
          <div className="badge">🔥 India Style Premium Ludo UI</div>
          <h2>Build Your <span>Professional Ludo Website</span></h2>
          <p>Online matches, wallet system, private turn timer, real queue countdown aur next-level game experience — sab ek hi platform par.</p>
          <div className="hero-actions">
            <button className="primary-btn" onClick={() => (profile?.mobile ? onStart() : onRequireLogin?.('start'))} type="button">Start Playing</button>
            {rejoinMatch?.roomId ? (
              <button className="secondary-btn" onClick={onRejoin} type="button">Rejoin Match</button>
            ) : (
              <button className="secondary-btn" type="button" onClick={() => { if (watchDemoUrl) window.open(watchDemoUrl, '_blank', 'noopener,noreferrer'); }}>Watch Demo</button>
            )}
          </div>
          {rejoinMatch?.roomId && (
            <div className="queue-helper-banner top-gap">Rejoin available: {rejoinMatch.mode || 'Active Match'} • Seat {rejoinMatch.seat || '-'} • Room {rejoinMatch.roomId}</div>
          )}
          <div className="stats-grid">
            <div className="stat-box"><h3>25K+</h3><p>Active Players</p></div>
            <div className="stat-box"><h3>₹ Rewards</h3><p>Coin Wallet System</p></div>
            <div className="stat-box"><h3>4P</h3><p>All Tables</p></div>
          </div>
        </motion.div>
        <motion.div className="hero-right" initial={{ opacity: 0, x: 40, scale: 0.9 }} animate={{ opacity: 1, x: 0, scale: 1 }} transition={{ duration: 0.7 }}>
          <div className="board-card">
            <div className="board-header"><span>Live Match Preview</span><span className="live-dot">● Live</span></div>
            <div className="mini-board">
              <div className="zone red-zone"></div>
              <div className="zone green-zone"></div>
              <div className="zone blue-zone"></div>
              <div className="zone yellow-zone"></div>
              <div className="center-star">★</div>
              <div className="token token-red"></div>
              <div className="token token-green"></div>
              <div className="token token-blue"></div>
              <div className="token token-yellow"></div>
            </div>
            <div className="match-info">
              <div className="player-row"><span>Rahul • Red</span><span>Turn: 6 🎲</span></div>
              <div className="progress-bar"><div className="progress-fill"></div></div>
            </div>
          </div>
        </motion.div>
      </section>
      <section className="cards-section">
        <div className="section-title"><h3>Game Features</h3><p>Premium modules jo tumhari Ludo website ko pro bana denge</p></div>
        <div className="cards-grid">
          {featureCards.map((card, index) => (
            <motion.div key={card.title} className={`feature-card ${card.color}`} initial={{ opacity: 0, y: 35 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.12 }}>
              <div className="feature-icon">{card.icon}</div>
              <h4>{card.title}</h4>
              <p>{card.subtitle}</p>
              <button onClick={() => onCardOpen(card.screen)} type="button">Open</button>
            </motion.div>
          ))}
        </div>
      </section>
    </>
  );
}

export default function App() {
  const [screen, setScreen] = useState(() => resolveScreenFromHash(window.location.hash || '', false));
  const [modes, setModes] = useState(defaultModes);
  const [selectedMode, setSelectedMode] = useState(defaultModes[1]);
  const [currentMatch, setCurrentMatch] = useState(() => {
    try {
      const raw = localStorage.getItem('ludo-current-match');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [profile, setProfile] = useState(() => {
    try {
      const raw = localStorage.getItem('ludo-profile');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const [pendingModeAfterLogin, setPendingModeAfterLogin] = useState(null);
  const [pendingScreenAfterLogin, setPendingScreenAfterLogin] = useState(null);
  const [adminToken, setAdminToken] = useState(() => {
    try { return localStorage.getItem('ludo-admin-token') || ''; } catch { return ''; }
  });
  const [activeRejoin, setActiveRejoin] = useState(null);
  const [joinChoiceMode, setJoinChoiceMode] = useState(null);
  const [walletConfig, setWalletConfig] = useState({ coinsPerRupee: 10, signupBonusEnabled: false, signupBonusCoins: 0, watchDemoUrl: '', packages: [] });
  const wallet = useWallet(profile);
  const { queueState, queueForMatch, cancelQueue, resetQueueState, isJoining } = useMatchmaking(profile, wallet.applyWalletUpdate);

  useEffect(() => {
    let cancelled = false;
    Promise.all([request('/api/public/tables'), request('/api/public/wallet-config').catch(() => null)])
      .then(([data, walletData]) => {
        if (cancelled || !data?.tables?.length) return;
        setModes(data.tables);
        if (walletData?.walletConfig) setWalletConfig(walletData.walletConfig);
        setSelectedMode((prev) => {
          if (!prev) return data.tables[0];
          return data.tables.find((item) => item.modeId === prev.modeId || item.title === prev.title) || data.tables[0];
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (profile) {
      localStorage.setItem('ludo-profile', JSON.stringify(profile));
      return;
    }
    localStorage.removeItem('ludo-profile');
  }, [profile]);

  useEffect(() => {
    if (adminToken) localStorage.setItem('ludo-admin-token', adminToken);
    else localStorage.removeItem('ludo-admin-token');
    const resolved = resolveScreenFromHash(window.location.hash || '', Boolean(adminToken));
    setScreen(resolved);
  }, [adminToken]);

  useEffect(() => {
    const syncFromHash = () => setScreen(resolveScreenFromHash(window.location.hash || '', Boolean(adminToken)));
    syncFromHash();
    window.addEventListener('hashchange', syncFromHash);
    return () => window.removeEventListener('hashchange', syncFromHash);
  }, [adminToken]);

  useEffect(() => {
    if (!currentMatch?.roomId) {
      localStorage.removeItem('ludo-current-match');
      return;
    }
    localStorage.setItem('ludo-current-match', JSON.stringify(currentMatch));
    const completed = Boolean(currentMatch?.gameState?.completed);
    if (completed) {
      localStorage.removeItem(LAST_ACTIVE_MATCH_ID_KEY);
      localStorage.setItem(LAST_MATCH_STATUS_KEY, 'completed');
      return;
    }
    localStorage.setItem(LAST_ACTIVE_MATCH_ID_KEY, currentMatch.roomId);
    localStorage.setItem(LAST_MATCH_STATUS_KEY, currentMatch?.gameState?.status || 'active');
  }, [currentMatch]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!profile?.mobile || !pendingModeAfterLogin) return;
    const modeToJoin = pendingModeAfterLogin;
    setPendingModeAfterLogin(null);
    setSelectedMode(modeToJoin);
    goTo('online');
    queueForMatch(modeToJoin);
  }, [profile?.mobile, pendingModeAfterLogin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (queueState.result?.roomId) {
      setCurrentMatch(queueState.result);
      localStorage.setItem('ludo-current-match', JSON.stringify(queueState.result));
      setScreen('board');
      window.location.hash = SCREEN_TO_HASH.board;
    }
  }, [queueState.result]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let cancelled = false;
    async function loadActiveMatchStatus() {
      if (!profile?.mobile) {
        setActiveRejoin(null);
        return;
      }
      try {
        const result = await request(`/api/matches/active/${encodeURIComponent(profile.mobile)}`);
        if (cancelled) return;
        if (!result?.hasActiveMatch || !result?.match?.roomId) {
          setActiveRejoin(null);
          setCurrentMatch(null);
          localStorage.removeItem('ludo-current-match');
          localStorage.removeItem(LAST_ACTIVE_MATCH_ID_KEY);
          localStorage.setItem(LAST_MATCH_STATUS_KEY, 'completed');
          return;
        }
        const match = result.match;
        const rejoinState = {
          roomId: result.matchId || match.roomId,
          status: result.status || 'active',
          seat: result.seat || match.playerId,
          mode: result.mode || 'Quick Table',
          match,
        };
        setActiveRejoin(rejoinState);
        resetQueueState('Active match found. Rejoin available.');
        const previousRoomId = localStorage.getItem(LAST_ACTIVE_MATCH_ID_KEY);
        const previousStatus = localStorage.getItem(LAST_MATCH_STATUS_KEY);
        const shouldAutoRoute = previousRoomId && previousRoomId === rejoinState.roomId && previousStatus !== 'completed';
        if (shouldAutoRoute) {
          setCurrentMatch(match);
          goTo('board');
        }
      } catch {
        // ignore boot-time active match check failures
      }
    }
    loadActiveMatchStatus();
    return () => {
      cancelled = true;
    };
  }, [profile?.mobile]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (screen === 'login' || screen === 'home' || screen.startsWith('admin')) return;
    if (!profile?.mobile && requiresUserLogin(screen)) {
      setPendingScreenAfterLogin((prev) => prev || screen);
      setScreen('login');
      if (window.location.hash !== SCREEN_TO_HASH.login) window.location.hash = SCREEN_TO_HASH.login;
    }
  }, [profile?.mobile, screen]);

  const pageTitle = useMemo(() => ({
    wallet: 'Wallet',
    withdraw: 'Withdraw',
    history: 'History',
    settings: 'Settings',
    login: 'Login',
    admin: 'Admin Overview',
    'admin-payments': 'Payment Requests',
    'admin-users': 'Admin Users',
    'admin-login': 'Admin Login',
    modes: 'Modes',
    board: 'Game Board',
    online: 'Online Lobby',
  }[screen] || 'Home'), [screen]);

  function goTo(nextScreen) {
    const safeScreen = nextScreen || 'home';
    if (!profile?.mobile && requiresUserLogin(safeScreen)) {
      setPendingScreenAfterLogin(safeScreen);
      setScreen('login');
      window.location.hash = SCREEN_TO_HASH.login;
      return;
    }
    setScreen(safeScreen);
    window.location.hash = SCREEN_TO_HASH[safeScreen] || '#/';
  }

  function openLoginFor(reason = null) {
    if (reason === 'wallet' || reason === 'withdraw') setPendingModeAfterLogin(null);
    if (reason && requiresUserLogin(reason)) setPendingScreenAfterLogin(reason);
    setScreen('login');
    window.location.hash = SCREEN_TO_HASH.login;
  }

  function handleAdminLoginSuccess(token) {
    setAdminToken(token);
    goTo('admin');
  }

  function handleAdminLogout() {
    if (adminToken) {
      request('/api/admin/auth/logout', { method: 'POST', headers: { Authorization: `Bearer ${adminToken}` } }).catch(() => {});
    }
    setAdminToken('');
    goTo('admin-login');
  }

  async function handleQueue(mode) {
    if (isJoining) return;
    setSelectedMode(mode);
    if (!profile?.mobile) {
      setPendingModeAfterLogin(mode);
      goTo('login');
      return;
    }
    const runningMatch = activeRejoin?.roomId ? activeRejoin : (currentMatch?.roomId ? {
      roomId: currentMatch.roomId,
      mode: currentMatch?.gameState?.modeTitle || selectedMode?.title || 'Active Table',
      seat: currentMatch.playerId,
      match: currentMatch,
    } : null);
    if (runningMatch?.roomId) {
      setJoinChoiceMode(mode);
      return;
    }
    setCurrentMatch(null);
    localStorage.removeItem('ludo-current-match');
    goTo('online');
    const result = await queueForMatch(mode);
    if (result?.code === 'ACTIVE_MATCH_EXISTS') {
      setJoinChoiceMode(mode);
    }
  }

  async function handleJoinNewTable() {
    if (!profile?.mobile || !joinChoiceMode) return;
    try {
      await request('/api/matches/leave-active', { method: 'POST', body: JSON.stringify({ mobile: profile.mobile }) });
    } catch {
      // ignore and continue with latest backend state
    }
    setJoinChoiceMode(null);
    setCurrentMatch(null);
    setActiveRejoin(null);
    localStorage.removeItem('ludo-current-match');
    localStorage.removeItem(LAST_ACTIVE_MATCH_ID_KEY);
    localStorage.setItem(LAST_MATCH_STATUS_KEY, 'completed');
    goTo('online');
    await queueForMatch(joinChoiceMode);
  }

  function handleLoginSuccess() {
    if (pendingModeAfterLogin) {
      goTo('online');
      return;
    }
    if (pendingScreenAfterLogin && requiresUserLogin(pendingScreenAfterLogin)) {
      const next = pendingScreenAfterLogin;
      setPendingScreenAfterLogin(null);
      goTo(next);
      return;
    }
    goTo('modes');
  }

  async function handleMatchComplete() {
    try {
      return await wallet.refreshWallet('Match result settled. Wallet refreshed instantly.');
    } catch {
      return null;
    }
  }

  async function handleRejoinMatch() {
    if (!profile?.mobile) {
      goTo('login');
      return;
    }
    try {
      const result = await request('/api/matches/rejoin', { method: 'POST', body: JSON.stringify({ mobile: profile.mobile }) });
      if (!result?.match?.roomId) {
        setActiveRejoin(null);
        localStorage.removeItem(LAST_ACTIVE_MATCH_ID_KEY);
        localStorage.setItem(LAST_MATCH_STATUS_KEY, 'completed');
        return;
      }
      setCurrentMatch(result.match);
      setActiveRejoin({
        roomId: result.match.roomId,
        status: result.status || 'active',
        seat: result.seat || result.match.playerId,
        mode: result.mode || 'Quick Table',
        match: result.match,
      });
      localStorage.setItem(LAST_ACTIVE_MATCH_ID_KEY, result.match.roomId);
      localStorage.setItem(LAST_MATCH_STATUS_KEY, 'active');
      goTo('board');
    } catch {
      setActiveRejoin(null);
    }
  }

  function clearRejoinStateAndMatch() {
    setCurrentMatch(null);
    setActiveRejoin(null);
    localStorage.removeItem('ludo-current-match');
    localStorage.removeItem(LAST_ACTIVE_MATCH_ID_KEY);
    localStorage.setItem(LAST_MATCH_STATUS_KEY, 'completed');
    resetQueueState('Lobby ready.');
    goTo('modes');
  }

  async function handleCancelQueue() {
    await cancelQueue(selectedMode);
    setCurrentMatch(null);
    localStorage.removeItem('ludo-current-match');
    goTo('modes');
  }

  const isBoardScreen = screen === 'board';

  return (
    <div className={`app ${isBoardScreen ? 'board-screen-app' : ''}`.trim()}>
      <div className="bg-orb orb-1"></div>
      <div className="bg-orb orb-2"></div>
      <div className="bg-orb orb-3"></div>

      {!isBoardScreen && (
      <header className="navbar navbar-with-wallet">
        <div className="brand">
          <div className="brand-logo">🎲</div>
          <div>
            <h1>Ludo Pro Max</h1>
            <p>Premium Multiplayer Experience</p>
          </div>
        </div>
        <nav className="nav-links">
          <NavButton active={screen === 'home'} onClick={() => goTo('home')}>Home</NavButton>
          <NavButton active={screen === 'modes'} onClick={() => goTo('modes')}>Modes</NavButton>
          <NavButton active={screen === 'online'} onClick={() => goTo('online')}>Online</NavButton>
          <NavButton active={screen === 'wallet' || screen === 'withdraw'} onClick={() => goTo('wallet')}>Wallet</NavButton>
          <NavButton active={screen === 'history'} onClick={() => goTo('history')}>History</NavButton>
          <NavButton active={screen === 'settings'} onClick={() => goTo('settings')}>Settings</NavButton>
          <NavButton active={screen === 'login'} onClick={() => goTo('login')}>{profile ? 'Profile' : 'Login'}</NavButton>
        </nav>
        <div className="nav-wallet-chip">
          <span className="tiny-label">Wallet Live</span>
          <strong>{wallet.balance.toLocaleString()} Coins</strong>
          <small>{wallet.lastSyncLabel}</small>
        </div>
      </header>
      )}

      <main className={`main-content ${isBoardScreen ? 'board-main-content' : ''}`.trim()}>
        {screen !== 'home' && screen !== 'board' && (
          <div className="page-heading-row">
            <div><div className="tiny-label">Premium UI Module</div><h2 className="page-heading">{pageTitle}</h2></div>
            <button className="secondary-btn" onClick={() => goTo('home')} type="button">Back to Home</button>
          </div>
        )}

        {screen === 'home' && <><HomeSection onCardOpen={goTo} onStart={() => goTo('modes')} rejoinMatch={activeRejoin} onRejoin={handleRejoinMatch} watchDemoUrl={walletConfig.watchDemoUrl} profile={profile} onRequireLogin={openLoginFor} /><DashboardTabs selectedMode={selectedMode} wallet={wallet} profile={profile} modes={modes} /></>}
        {screen === 'modes' && <ModeSelection modes={modes} onQueue={handleQueue} activeModeTitle={selectedMode?.title || queueState.queuedModeTitle} isJoining={isJoining} />}
        {screen === 'online' && <MatchmakingScreen mode={selectedMode} profile={profile} queueState={queueState} isJoining={isJoining} onFindMatch={() => handleQueue(selectedMode)} onCancelQueue={handleCancelQueue} onPlayNow={() => goTo('board')} rejoinMatch={activeRejoin} onRejoin={handleRejoinMatch} />}
        {screen === 'wallet' && <WalletScreen wallet={wallet} walletConfig={walletConfig} onBuy={wallet.buyCoins} profile={profile} onRequireLogin={openLoginFor} onWalletRefresh={wallet.refreshWallet} initialTab="recharge" />}
        {screen === 'withdraw' && <WalletScreen wallet={wallet} walletConfig={walletConfig} onBuy={wallet.buyCoins} profile={profile} onRequireLogin={openLoginFor} onWalletRefresh={wallet.refreshWallet} initialTab="withdraw" />}
        {screen === 'history' && <HistoryScreen transactions={wallet.transactions} />}
        {screen === 'settings' && <SettingsScreen profile={profile} />}
        {screen === 'login' && <LoginScreen profile={profile} onLogin={setProfile} onLoginSuccess={handleLoginSuccess} pendingModeTitle={pendingModeAfterLogin?.title || null} />}
        {screen === 'admin-login' && <AdminLoginScreen onLogin={handleAdminLoginSuccess} />}
        {['admin', 'admin-payments', 'admin-users'].includes(screen) && <AdminScreen modes={modes} onModesUpdate={setModes} walletConfig={walletConfig} setWalletConfig={setWalletConfig} onWalletConfigSaved={setWalletConfig} adminToken={adminToken} onAdminLogout={handleAdminLogout} routeSection={screen === 'admin-payments' ? 'payments' : (screen === 'admin-users' ? 'users' : 'overview')} onNavigate={goTo} />}
        {screen === 'board' && (
          <GameBoard
            onBack={() => goTo('modes')}
            onResultClose={clearRejoinStateAndMatch}
            selectedMode={selectedMode}
            currentMatch={currentMatch}
            profile={profile}
            walletBalance={wallet.balance}
            walletStatus={wallet.lastAction}
            onMatchComplete={handleMatchComplete}
          />
        )}
      </main>
      <ActiveMatchChoiceModal
        open={Boolean(joinChoiceMode && (activeRejoin?.roomId || currentMatch?.roomId))}
        mode={joinChoiceMode}
        activeRejoin={activeRejoin || { roomId: currentMatch?.roomId, mode: selectedMode?.title || 'Active Table', seat: currentMatch?.playerId }}
        onContinue={() => {
          setJoinChoiceMode(null);
          handleRejoinMatch();
        }}
        onJoinNew={handleJoinNewTable}
        onClose={() => setJoinChoiceMode(null)}
      />
    </div>
  );
}
